import React, { FC, memo } from "react";
import { useCurrencySymbol } from "hooks/useCurrencySymbol";
import { IconType } from "enums/Icon";
import { List, Map } from "immutable";
import { useTranslation } from "react-i18next";
import { connect } from "react-redux";
import { createStructuredSelector } from "reselect";
import Icon from "../../../../components/Icon/Icon";
import { getAddonsCost } from "../../../../components/TotalCostHint/totalCostHelper";
import useFetchOnceEffect from "../../../../hooks/useFetchOnceEffect";
import { getRequestTypes } from "../../../../reduxFolder/reducers/serviceRequests";
import { requestsSelectServiceTypes } from "../../../../reduxFolder/selectors/serviceRequestsSelectors";
import { servicesSelectAddons } from "../../../../reduxFolder/selectors/servicesSelectors";
import {
  ArrowIcon,
  ParcelButton,
  ServicesSection,
  Status,
} from "../../../Storage/components/ServiceButtons/ServiceButtons.styles";
import { CustomRasterIcon } from "../../../Storage/components/ServiceRequests/ServiceRequest.styles";

interface ExpectedServicesButtonsProps {
  expectedItem: any;
  openServicePanel: (requestType: string) => void;
  openPackOptions: () => void;
  serviceRequestsTypes: List<any>;
  getRequestTypes: () => Promise<any>;
  addons: any;
}

const ExpectedServicesButtons: FC<ExpectedServicesButtonsProps> = memo(
  ({
    expectedItem,
    openServicePanel,
    openPackOptions,
    serviceRequestsTypes,
    getRequestTypes,
    addons,
  }) => {
    const { t } = useTranslation("common");
    const currencySymbol = useCurrencySymbol();
    useFetchOnceEffect(!serviceRequestsTypes?.size, getRequestTypes);

    if (!serviceRequestsTypes?.size) return null;

    const rawRequests = expectedItem.get("service_requests");
    const activeRequests: any[] = rawRequests
      ? rawRequests.toJS
        ? rawRequests.toJS()
        : rawRequests
      : [];
    const serviceRequestTypeIds: number[] = activeRequests.map(
      (request: any) => request.service_request_type_id,
    );

    const currentAddons = expectedItem.get("expected_item_addons");
    const addonIds = currentAddons
      ? currentAddons.toJS
        ? currentAddons.toJS()
        : currentAddons
      : [];
    const expectedAddons = addons.map((addon: any) =>
      addon.set("id", addon.get("code")),
    );
    const addonsCost = getAddonsCost(
      List([Map({ addons: addonIds })]),
      expectedAddons,
    );

    return (
      <div>
        {serviceRequestsTypes.map((req) => {
          const isComplexType = !!req.get("complexType");
          const isActive = isComplexType
            ? req
                .get("requestKeys", List())
                .some((sub: any) =>
                  serviceRequestTypeIds.includes(sub.get("id")),
                )
            : serviceRequestTypeIds.includes(req.get("id"));

          return (
            <ServicesSection key={req.get("type")}>
              <ParcelButton onClick={() => openServicePanel(req.get("type"))}>
                <div>
                  {!!req.get("icon") && (
                    <CustomRasterIcon
                      $iconUrl={req.get("icon")}
                      className="icon"
                    />
                  )}
                  {req.get("title")}
                </div>
                {isActive && (
                  <Status>
                    {t("serviceRequests.states.pending_translation")}
                  </Status>
                )}
                <ArrowIcon type={IconType.Arrow} />
              </ParcelButton>
            </ServicesSection>
          );
        })}
        <ServicesSection>
          <ParcelButton onClick={openPackOptions}>
            <div>
              <Icon type={IconType.PackOptions} />
              {t("serviceRequests.packOptions.title")}
            </div>
            {!!addonsCost && (
              <Status>
                {currencySymbol} {addonsCost}
              </Status>
            )}
            <ArrowIcon type={IconType.Arrow} />
          </ParcelButton>
        </ServicesSection>
      </div>
    );
  },
);

const withConnect = connect(
  createStructuredSelector({
    serviceRequestsTypes: requestsSelectServiceTypes,
    addons: servicesSelectAddons,
  }),
  {
    getRequestTypes,
  },
);

export default withConnect(ExpectedServicesButtons);
